import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { adminBroadcastsService } from '@/services/admin-broadcasts.service';
import { ApiError } from '@/services/http';
import type { BroadcastView } from '@/types/api';

export function AdminBroadcastsPage() {
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState<BroadcastView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminBroadcastsService
      .list()
      .then(setBroadcasts)
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not load broadcasts.'),
      )
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Subscribers</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Broadcasts
            </h1>
            <p className="mt-1 max-w-xl text-[13px] text-or-slate-600">
              Send a template to a list in bulk. Each send is paced through the Postmark broadcast
              stream and tracked here.
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate('/admin/subscribers/broadcasts/new')}
          >
            <Plus className="h-4 w-4" aria-hidden />
            New broadcast
          </Button>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-or-slate-200 bg-white">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-or-slate-50 text-[11px] uppercase tracking-[0.08em] text-or-slate-500">
              <tr>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">List</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Recipients</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && broadcasts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-or-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : broadcasts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-or-slate-500">
                    No broadcasts yet.
                  </td>
                </tr>
              ) : (
                broadcasts.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => navigate(`/admin/subscribers/broadcasts/${b.id}`)}
                    className="cursor-pointer border-t border-or-slate-100 hover:bg-or-slate-50"
                  >
                    <td className="px-4 py-3 font-semibold text-or-slate-900">{b.subject}</td>
                    <td className="px-4 py-3 text-or-slate-700">{b.listName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <BroadcastStatusChip status={b.status} />
                    </td>
                    <td className="px-4 py-3 text-or-slate-700">
                      {b.recipientCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-or-slate-400">
                      {new Date(b.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

export function BroadcastStatusChip({ status }: { status: string }) {
  if (status === 'sent') return <Chip variant="live">Sent</Chip>;
  if (status === 'sending' || status === 'queued') return <Chip variant="pending">{status}</Chip>;
  if (status === 'failed') return <Chip variant="default">Failed</Chip>;
  if (status === 'canceled') return <Chip variant="default">Canceled</Chip>;
  return <Chip variant="default">Draft</Chip>;
}
